import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { Input } from '@/components/ui/input';
import { BackButton } from '@/components/ui/app/back-button';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { CheckCircle } from 'lucide-react';
import { getErrorMessage } from '@/lib/utils/validation-errors';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export function ForgotPasswordForm({ onBack, onSwitchToLogin }) {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email) {
            toast.error(t('welcome:veuillezSaisirVotreAdresseEmail'));
            return;
        }

        setIsSubmitting(true);
        try {
            await horodatyApi.resetPassword({ email, source: 'v2' });
            setIsSuccess(true);
        } catch (error) {
            const message = getErrorMessage(error, t('welcome:resetLinkFailed'));
            toast.error(message.split('\n')[0]);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSuccess) {
        return (
            <div className="relative flex flex-col min-h-full">
                <div className="shrink-0">
                    <BackButton onClick={onBack} />
                </div>

                <div className="flex flex-col items-center justify-center flex-1 max-w-md mx-auto w-full text-center py-8">
                    <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mb-6">
                        <CheckCircle className="w-8 h-8 text-success" />
                    </div>

                    <h2 className="text-4xl font-black mb-4 text-center">{t('welcome:emailEnvoye')}</h2>

                    <p className="text-muted-foreground mb-8">
                        {t('welcome:siUnCompteExisteAvecLAdresse')} <span className="font-semibold">{email}</span>{t('welcome:vousRecevrezUnEmailAvecUnLienPour')}
                    </p>

                    <p className="text-sm text-muted-foreground mb-8">
                        {t('welcome:pensezAVerifierVosSpamsSiVousNe')}
                    </p>

                    <Button variant="link" color="secondary" onClick={onSwitchToLogin} className="font-medium">
                        {t('welcome:retourALaConnexion')}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="relative flex flex-col min-h-full">
            <div className="shrink-0">
                <BackButton onClick={onBack} />
            </div>

            <div className="flex flex-col items-center justify-center flex-1 max-w-md mx-auto w-full py-8">
                <h2 className="text-4xl font-black mb-4 text-center">{t('welcome:motDePasseOublie')}</h2>

                <p className="text-muted-foreground text-center mb-8">
                    {t('welcome:entrezVotreAdresseEmailEtNousVousEnverrons')}
                </p>

                <form className="w-full space-y-4" onSubmit={handleSubmit}>
                    <Input
                        type="email"
                        placeholder={t('welcome:email')}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />

                    <SubmitButton
                        isSubmitting={isSubmitting}
                        loadingText={t('welcome:envoiEnCours')}
                        color="primary"
                        size="cta"
                        className="w-full mt-6"
                    >
                        {t('welcome:envoyerLeLien')}
                    </SubmitButton>
                </form>

                <p className="mt-6 text-muted-foreground">
                    {t('welcome:vousVousSouvenezDeVotreMotDePasse')}{' '}
                    <Button
                        variant="link"
                        color="secondary"
                        onClick={onSwitchToLogin}
                        className="font-medium p-0 h-auto"
                    >
                        {t('welcome:seConnecter')}
                    </Button>
                </p>
            </div>
        </div>
    );
}
